import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Chip, Avatar, useTheme, Button, Tooltip, IconButton } from '@mui/material';
import { EventBusy, AutoFixHigh, Refresh } from '@mui/icons-material';
import LoadingState from '../shared/LoadingState';
import EmptyState from '../shared/EmptyState';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import { parseUtc } from '../../utils/time';


const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const ScheduleView = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [events, setEvents] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState('all');
  const [viewMode, setViewMode] = useState('schedule'); // 'schedule' | 'completed'
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    loadScheduleData();
  }, [viewMode]);

  const loadScheduleData = async () => {
    setLoading(true);
    try {
      // Completed mode pulls done work for the register calendar;
      // schedule mode pulls the live board. External calendar events
      // only make sense on the schedule view — done work shouldn't be
      // polluted with gcal reference entries.
      const jobsUrl = viewMode === 'completed'
        ? '/admin/jobs?status=Completed'
        : '/admin/jobs';
      const [jobsRes, workersRes, calendarRes] = await Promise.all([
        api.get(jobsUrl).catch(() => ({ data: [] })),
        api.get('/admin/employees').catch(() => ({ data: [] })),
        api.get('/admin/calendar/events').catch(() => ({ data: { events: [] } }))
      ]);
      
      const jobs = jobsRes.data || [];
      const employeeMap = {};
      (workersRes.data || []).forEach(w => employeeMap[w.id] = w);
      setWorkers(workersRes.data || []);

      const jobEvents = jobs.filter(j => j.scheduled_date).map(job => {
        // scheduled_date + scheduled_time — the old code ignored the time,
        // so every job rendered at date-default and ordering was fiction.
        const start = new Date(job.scheduled_date);
        if (job.scheduled_time) {
          const [hh, mm] = String(job.scheduled_time).slice(0, 5).split(':').map(Number);
          start.setHours(hh || 0, mm || 0, 0, 0);
        }
        const end = new Date(start.getTime() + (job.estimated_duration_mins || 60) * 60000);
        const client = (job.client_name && job.client_name !== 'Unknown') ? job.client_name : null;
        const loc = (job.location || '').split(',')[0];
        const title = client ? `${client} — ${loc}` : (loc || 'Untitled Job');
        return {
          id: `job-${job.id}`,
          title,
          start,
          end,
          resource: job,
          worker: employeeMap[job.employee_id],
          status: job.status,
          type: 'job'
        };
      });

      const externalEvents = viewMode === 'completed' ? [] :
        (calendarRes.data?.events || []).map(event => ({
        id: `ext-${event.id}`,
        title: event.summary,
        start: parseUtc(event.start),
        end: parseUtc(event.end),
        location: event.location,
        description: event.description,
        type: 'external',
        status: 'Confirmed'
      }));

      setEvents([...jobEvents, ...externalEvents]);
    } catch (err) {
      console.error('Failed to load schedule:', err);
    }
    setLoading(false);
  };

  const handleOptimizeRoute = async () => {
    if (selectedWorker === 'all') return;
    
    setOptimizing(true);
    try {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const response = await api.post(`/admin/employees/${selectedWorker}/optimize-route?date_str=${dateStr}`);
      if (response.data.ok) {
        alert(`Successfully optimized ${response.data.updated_count} jobs.`);
        loadScheduleData();
      }
    } catch (err) {
      console.error('Failed to optimize route:', err);
      alert('Optimization failed. Check console for details.');
    } finally {
      setOptimizing(false);
    }
  };

  const handleSelectEvent = (event) => {
    if (event.type === 'job') {
      navigate(`/jobs/${event.resource.id}`);
    } else {
      alert(`External Event: ${event.title}\nLocation: ${event.location || 'N/A'}\nDescription: ${event.description || 'N/A'}`);
    }
  };

  const STATUS_COLORS = {
    'New Request': '#64748B',
    'Needs Info': '#9CA3AF',
    'Tentative': '#3B82F6',
    'Locked': '#EAB308',
    'Pending': '#EAB308',
    'Confirmed': '#22C55E',
    'Scheduled': '#3B82F6',
    'Failed': '#EF4444',
    'Completed': '#8B5CF6',
    'Waiting on Client': '#F97316',
    'En Route': '#06B6D4',
    'On Site': '#4ADE80',
    'Rescheduled': '#F97316',
    'Cancelled': '#9CA3AF'
  };

  const eventStyleGetter = (event, start, end, isSelected) => {
    if (event.type === 'external') {
      return {
        style: {
          backgroundColor: '#E2E8F0',
          borderRadius: '4px',
          opacity: 0.8,
          color: '#475569',
          border: '1px dashed #94A3B8',
          display: 'block'
        }
      };
    }

    const status = event.resource?.status || 'Needs Info';
    const color = STATUS_COLORS[status] || '#9CA3AF';

    return {
      style: {
        backgroundColor: color,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  const filteredEvents = selectedWorker === 'all' 
    ? events 
    : events.filter(evt => evt.type === 'external' || evt.resource?.employee_id === parseInt(selectedWorker));

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
       <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
            <Typography variant="h5" fontWeight={800} color="text.primary">
                {viewMode === 'completed' ? 'Completed Work' : 'Schedule'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {viewMode === 'completed'
                    ? 'Done work on the calendar — what got completed, when, by whom'
                    : 'Manage team availability and job assignments'}
            </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box sx={{ display: 'flex', border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden' }}>
                {['schedule', 'completed'].map(m => (
                    <button key={m} onClick={() => setViewMode(m)} style={{
                        padding: '7px 14px', border: 'none', cursor: 'pointer', fontWeight: 600,
                        fontSize: '0.85rem',
                        background: viewMode === m ? '#2563EB' : 'white',
                        color: viewMode === m ? 'white' : '#475569',
                    }}>{m === 'schedule' ? 'Schedule' : 'Completed'}</button>
                ))}
            </Box>
            <Tooltip title="Refresh Data">
                <span>
                    <IconButton onClick={loadScheduleData} disabled={loading}>
                        <Refresh />
                    </IconButton>
                </span>
            </Tooltip>
            
            {selectedWorker !== 'all' && (
                <Button 
                    variant="contained" 
                    size="small"
                    startIcon={optimizing ? <Refresh className="spin-animation" /> : <AutoFixHigh />}
                    onClick={handleOptimizeRoute}
                    disabled={optimizing}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                    {optimizing ? 'Optimizing...' : 'Optimize Route'}
                </Button>
            )}

            <select 
                value={selectedWorker} 
                onChange={(e) => setSelectedWorker(e.target.value)}
                style={{ 
                    padding: '8px 12px', 
                    borderRadius: '8px', 
                    border: '1px solid #E2E8F0',
                    backgroundColor: 'white',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    outline: 'none',
                    cursor: 'pointer'
                }}
            >
                <option value="all">All Workers</option>
                {workers.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                ))}
            </select>
        </Box>
      </Box>

      {loading && !events.length ? (
        <LoadingState message="Loading schedule..." />
      ) : filteredEvents.length === 0 ? (
        <Paper elevation={0} sx={{ p: 2, flexGrow: 1, border: '1px solid #E2E8F0', borderRadius: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <EmptyState 
                icon={<EventBusy />}
                title={selectedWorker === 'all' ? 'No scheduled jobs' : 'No jobs for this worker'}
                description={selectedWorker === 'all' ? 'Jobs will appear here once scheduled.' : 'Select another worker or assign jobs to them.'}
                actionLabel={selectedWorker === 'all' ? 'Go to Job Pool' : null}
                onAction={() => navigate('/jobs')}
            />
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ p: 2, flexGrow: 1, border: '1px solid #E2E8F0', borderRadius: 3 }}>
          <Calendar
            localizer={localizer}
            events={filteredEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            onNavigate={(date) => setCurrentDate(date)}
            date={currentDate}
            views={['month', 'week', 'day', 'agenda']}
            defaultView='week'
            components={{
              event: ({ event }) => (
                <Box sx={{ fontSize: '0.75rem', p: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
                    {event.title}
                  </Typography>
                  {event.worker && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Avatar sx={{ width: 16, height: 16, mr: 0.5, fontSize: '10px' }}>
                            {event.worker.name[0]}
                        </Avatar>
                        <Typography variant="caption" noWrap>
                            {event.worker.name.split(' ')[0]}
                        </Typography>
                    </Box>
                  )}
                </Box>
              )
            }}
          />
        </Paper>
      )}
      <style>{`
        .spin-animation {
            animation: spin 1s linear infinite;
        }
        @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default ScheduleView;
